import { Col } from "../Layout/Grid";
import CircularProgress from "@mui/material/CircularProgress";

import { ChatMarkdown } from "../Chat/ChatMarkdown";
import { ResponseCard } from "./ResponseCard";
import {
  ChatMessage,
  ImageResponseCard,
  MarkdownWithToolsMessage,
} from "../ChatBox/types";
import React, { ReactNode, useState } from "react";
import { useContainerWidth } from "./utils";
import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";
import { speakerTint } from "./speakerTint";
import {
  MESSAGE_INDENT,
  SPEAKER_RULE_PADDING,
  SPEAKER_RULE_WIDTH,
  USER_BUBBLE_RADIUS,
  flushMarkdown,
} from "./chatLayout";

export type CustomPayload = {
  type: "ImageResponseCard";
  data: ImageResponseCard;
};

function ChatMessageBody({
  message: { data },
  onResponseButtonClick,
  isStreaming,
}: {
  message: ChatMessage;
  onResponseButtonClick?: (value?: string) => void;
  isStreaming?: boolean;
}) {
  switch (data.type) {
    case "markdown": {
      const tools = "tools" in data ? (data as MarkdownWithToolsMessage).tools : undefined;
      return (
        <div data-cy="agent-chat-markdown">
          <ChatMarkdown isStreaming={isStreaming} tools={tools}>{data.message}</ChatMarkdown>
        </div>
      );
    }
    case "react": {
      return <div data-cy="agent-chat-react">{data.message}</div>;
    }
    case "error": {
      return (
        <div
          style={{
            backgroundColor: "rgba(255, 0, 0, 0.5)",
            borderRadius: "8px",
            padding: "10px",
          }}
          data-cy="agent-chat-error"
        >
          {data.message}
        </div>
      );
    }
    case "imageResponseCard": {
      return (
        <div
          data-cy="agent-chat-image"
          style={{ marginTop: 16, marginBottom: 16 }}
        >
          <ResponseCard
            imageResponseCard={data.message}
            onResponseButtonClick={onResponseButtonClick}
          />
        </div>
      );
    }
    case "spinner": {
      return (
        <div data-cy="agent-chat-spinner">
          {/* Renders inline in the (white) chat message list next to "Working…";
              the old white color made it invisible. Use the theme primary. */}
          <CircularProgress size={20} color="primary" />
        </div>
      );
    }
    default:
      const _exhaustiveCheck: never = data;
      throw Error(`Unhandled message type ${JSON.stringify(_exhaustiveCheck)}`);
  }
}

const ChatMessageItem = React.memo(
  function ChatMessageItem({
    message,
    onResponseButtonClick,
    showAvatarColumn = true,
    isStreaming,
  }: {
    message: ChatMessage;
    onResponseButtonClick?: (value?: string) => void;
    showAvatarColumn?: boolean;
    isStreaming?: boolean;
  }) {
    const body = (
      <ChatMessageBody
        message={message}
        onResponseButtonClick={onResponseButtonClick}
        isStreaming={isStreaming}
      />
    );
    // Text starts under the speaker's name. On a narrow panel there is no
    // room to spare, so it runs full width under an inline head.
    const indent = showAvatarColumn ? MESSAGE_INDENT : 0;

    // The user's turn: directly under their name, right-aligned, in one
    // consistent bubble — not indented like a responder's text, which would
    // leave it floating away from the name it belongs to. Capped short of full width; responders keep the
    // full width they need for tables and charts.
    if (message.type === "user") {
      return (
        <Col span={24}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Box
              data-testid="chat-bubble-user"
              data-align="right"
              sx={{
                ...flushMarkdown,
                maxWidth: "80%",
                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                borderRadius: `${USER_BUBBLE_RADIUS}px`,
                px: 1.5,
                py: 0.75,
                overflowWrap: "anywhere",
              }}
            >
              {body}
            </Box>
          </Box>
        </Col>
      );
    }

    // A persistent agent answering in its own voice. Aligned exactly like the
    // assistant's text, with a thin rule in the agent's accent in the gutter
    // — enough to tell two agents apart without a competing background.
    if (message.speaker) {
      const { accent } = speakerTint(message.speaker.key, message.speaker.color);
      return (
        <Col span={24}>
          <Box
            data-testid="chat-bubble-speaker"
            data-speaker={message.speaker.key}
            data-accent={accent}
            sx={{
              ...flushMarkdown,
              ml: `${Math.max(indent - SPEAKER_RULE_WIDTH - SPEAKER_RULE_PADDING, 0)}px`,
              borderLeft: `${SPEAKER_RULE_WIDTH}px solid ${accent}`,
              pl: `${SPEAKER_RULE_PADDING}px`,
            }}
          >
            {body}
          </Box>
        </Col>
      );
    }

    return (
      <Col span={24}>
        <Box data-testid="chat-body-assistant" sx={{ pl: `${indent}px` }}>
          {body}
        </Box>
      </Col>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.message === nextProps.message &&
      prevProps.onResponseButtonClick === nextProps.onResponseButtonClick &&
      prevProps.showAvatarColumn === nextProps.showAvatarColumn &&
      prevProps.isStreaming === nextProps.isStreaming
    );
  }
);

export { ChatMessageItem };
